import { Link } from 'react-router-dom';
import type { CaseItem } from '@/mocks/cases';

interface Props {
  current: CaseItem;
  all: CaseItem[];
}

export default function RelatedCases({ current, all }: Props) {
  // 優先同類別，不足再補其他類別，最多顯示 3 筆
  const sameCategory = all.filter(
    (c) => c.id !== current.id && c.category === current.category,
  );
  const others = all.filter(
    (c) => c.id !== current.id && c.category !== current.category,
  );
  const related = [...sameCategory, ...others].slice(0, 3);

  if (related.length === 0) return null;

  return (
    <section className="bg-[#f4f2ef] py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-3">
              Related Work
            </p>
            <h2
              className="font-bold text-[#1a1a1a] leading-[1.35]"
              style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.75rem)' }}
            >
              你可能也感興趣的作品
            </h2>
          </div>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 text-[#b8956a] font-medium hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-[0.9375rem]"
          >
            瀏覽全部案例
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {related.map((item) => (
            <Link
              key={item.id}
              to={`/cases/${item.id}`}
              className="group block relative overflow-hidden rounded-2xl cursor-pointer"
            >
              <div className="w-full h-[280px] md:h-[320px]">
                <img
                  src={item.coverImg}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent"></div>

              {/* Category tag */}
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-[#b8956a] text-white text-xs font-medium rounded-full">
                  {item.category}
                </span>
              </div>

              {/* Hover arrow */}
              <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-arrow-right-up-line text-white text-sm"></i>
              </div>

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3
                  className="text-white font-semibold mb-1.5 leading-[1.4]"
                  style={{ fontSize: '1.0625rem' }}
                >
                  {item.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed line-clamp-1">
                  {item.subtitle}
                </p>
                <div className="flex items-center gap-2 mt-2.5 text-white/50 text-xs">
                  <i className="ri-map-pin-line"></i>
                  <span>{item.location}</span>
                  <span className="mx-1">·</span>
                  <i className="ri-aspect-ratio-line"></i>
                  <span>{item.area}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
